import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, startWith } from 'rxjs/operators';
import {
  CommonService,
  CONSTANT,
  LoaderService,
  OrderService,
} from '@bst-frontend/shared';

/* =======================
   MODELS
======================= */

interface PickupItem {
  productId: string | null;
  subItemId: string | null;
  quantityBox: number | null;
  weightKg: number | null;
  price: number | null;
  bstOfficePrice: number | null;
  _id?: string | null;
}

@Component({
  selector: 'bst-frontend-part-load-pickup-location-modal',
  templateUrl: './part-load-pickup-location-modal.component.html',
  styleUrls: ['./part-load-pickup-location-modal.component.css'],
})
export class PartLoadPickupLocationModalComponent implements OnInit {
  @Input() orderId!: string;
  @Input() dataToEdit: any;

  @Input() userAddresses: any[] = [];
  @Input() companyAddresses: any[] = [];
  @Input() productData: any[] = [];
  @Input() config: any;
  @Input() orderData: any;
  @Input() index: number | null = null;

  form!: FormGroup;
  addressList: any[] = [];
  pickupLocationOptions: string[] = CONSTANT.pickupLocationOptions;
  isViewMode = false;

  constructor(
    private fb: FormBuilder,
    private activeModal: NgbActiveModal,
    private orderService: OrderService,
    private loader: LoaderService,
    private commonService: CommonService
  ) {}

  /* =======================
     INIT
  ======================= */

  ngOnInit(): void {
    this.addressList = this.userAddresses;

    this.form = this.fb.group({
      type: ['Customer Address', Validators.required],
      pickup: this.buildPickup(),
    });

    if (this.dataToEdit?.pickup) {
      this.patchPickup(this.dataToEdit.pickup);
    }
  }

  /* =======================
     FORM BUILDERS
  ======================= */

  private buildPickup(): FormGroup {
    return this.fb.group({
      deliveryLocationId: [null, Validators.required],
      items: this.fb.array([this.buildItem()]),
    });
  }

  private buildItem(item?: Partial<PickupItem>): FormGroup {
    const fg = this.fb.group({
      productId: [item?.productId ?? null, Validators.required],
      subItemId: [{ value: item?.subItemId ?? null, disabled: true }],
      quantityBox: [
        item?.quantityBox ?? null,
        [Validators.required, Validators.min(1)],
      ],
      weightKg: [
        item?.weightKg ?? null,
        [Validators.required, Validators.min(1)],
      ],
      _id: [item?._id ?? null],
    });

    this.handleProductChange(fg);
    return fg;
  }

  /* =======================
     REACTIVE LOGIC
  ======================= */

  private handleProductChange(itemFG: FormGroup): void {
    const productCtrl = itemFG.get('productId')!;
    const subItemCtrl = itemFG.get('subItemId')!;

    productCtrl.valueChanges
      .pipe(startWith(productCtrl.value))
      .subscribe((productId) => {
        const subItems = this.getSubItems(productId);

        if (subItems.length) {
          subItemCtrl.enable({ emitEvent: false });
          subItemCtrl.setValidators(Validators.required);
        } else {
          subItemCtrl.reset(null, { emitEvent: false });
          subItemCtrl.clearValidators();
          subItemCtrl.disable({ emitEvent: false });
        }

        subItemCtrl.updateValueAndValidity({ emitEvent: false });
      });
  }

  /* =======================
     GETTERS
  ======================= */

  get pickupFG(): FormGroup {
    return this.form.get('pickup') as FormGroup;
  }

  get itemsFA(): FormArray {
    return this.pickupFG.get('items') as FormArray;
  }

  /* =======================
     ARRAY OPS
  ======================= */

  addItem(): void {
    this.itemsFA.push(this.buildItem());
  }

  removeItem(index: number): void {
    if (this.itemsFA.length > 1) {
      this.itemsFA.removeAt(index);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  /* =======================
     ADDRESS TYPE
  ======================= */

  onTypeChange(type: string): void {
    this.addressList =
      type === 'Customer Address' ? this.userAddresses : this.companyAddresses;

    this.pickupFG.get('deliveryLocationId')?.reset();
  }

  /* =======================
     PATCH EDIT DATA
  ======================= */

  private patchPickup(pickup: any): void {
    this.pickupFG.patchValue({
      deliveryLocationId: pickup.address?.addressId ?? null,
    });

    this.itemsFA.clear();

    pickup.items.forEach((it: any) => {
      this.itemsFA.push(
        this.buildItem({
          productId: it.product?.productId ?? null,
          bstOfficePrice: it.product?.bstOfficePrice ?? null,
          price: it.product?.price ?? null,
          subItemId: it.product?.subItems?.productId ?? null,
          quantityBox: it.planned?.qty ?? null,
          weightKg: it.planned?.weight ?? null,
          _id: it._id ?? null,
        })
      );
    });
  }

  /* =======================
     HELPERS
  ======================= */

  getSubItems(productId: string | null): any[] {
    if (!productId) return [];
    return this.productData.find((p) => p._id === productId)?.subItems || [];
  }

  private compactAddress(a: any) {
    if (!a) return null;
    const {
      addressId,
      companyName,
      contactName,
      contactNumber,
      pincode,
      city,
      locality,
      buildingName,
      landmark,
      type,
    } = a;

    return {
      addressId,
      companyName,
      contactName,
      contactNumber,
      pincode,
      city,
      locality,
      buildingName,
      landmark,
      type,
    };
  }

  /* =======================
     PAYLOAD
  ======================= */

  preparePayload() {
    const v = this.form.value;

    const addr = this.addressList.find(
      (a) => a.addressId === v.pickup.deliveryLocationId
    );

    return {
      pickup: {
        address: this.compactAddress(addr),
        items: v.pickup.items.map((it: PickupItem) => {
          const prod = this.productData.find((p) => p._id === it.productId);
          const sub = prod?.subItems?.find((s: any) => s._id === it.subItemId);

          return {
            product: {
              productId: prod?._id,
              code: prod?.code,
              name: prod?.name,
              price: prod?.price,
              bstOfficePrice: prod?.bstOfficePrice,
              subItems: sub
                ? {
                    productId: sub._id,
                    code: sub.code,
                    name: sub.name,
                    price: sub.price,
                    bstOfficePrice: sub.bstOfficePrice,
                  }
                : null,
            },
            planned: {
              qty: Number(it.quantityBox),
              weight: Number(it.weightKg),
            },
            _id: it._id ?? undefined,
          };
        }),
      },
    };
  }

  /* =======================
     SAVE / CANCEL
  ======================= */

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.preparePayload();
    this.isViewMode = true;
    this.loader.show();

    const req$ = this.dataToEdit
      ? this.orderService.updateOrderItemV2(
          this.orderId,
          this.dataToEdit._id,
          payload
        )
      : this.orderService.createOrderItemV2(this.orderId, payload);

    req$.pipe().subscribe({
      next: (res: any) => {
        const response = res.data;
        this.loader.show();
        let subOrders = [];
        subOrders = this.orderData.subOrders;
        if (this.index !== null) {
          // console.log('inside ', subOrders)
          subOrders[this.index] = response;
        } else {
          subOrders.push(response);
        }
        const orderPayload: any = {};
        const otherCharges = [];
        const calculateData = this.commonService.calculateFareFromSubOrders(
          subOrders,
          this.orderData.type,
          this.config
        );
        if (calculateData.discount) {
          otherCharges.push(calculateData.discount);
        }

        if (calculateData.minimumOrderFee) {
          otherCharges.push(calculateData.minimumOrderFee);
        }
        otherCharges.push(calculateData.tempoFee);
        orderPayload.otherCharges = otherCharges;
        orderPayload.totals = calculateData.totals;
        this.orderService
          .updateOrder(this.orderData._id, orderPayload)
          .subscribe({
            next: (res) => {
              this.isViewMode = false;
              this.loader.hide();
              console.log('Order updated', res);
              this.activeModal.close({ status: 'saved', data: res });
            },
            error: (err) => {
              this.isViewMode = false;
              this.loader.hide();
              this.activeModal.close({ status: 'saved', data: res });
              console.error('Update failed', err);
            },
          });
      },
      error: (err) => console.error(err),
    });
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
